import { useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import type { MonthlyBalance } from "@/hooks/useTransactions";
import { parseDateLocal } from "@/utils/dateUtils";
import { flowArrow, formatCurrency, formatDate, formatSignedCurrency } from "@/utils/formatters";

export interface PositionTrendDescription {
  firstMonth: string;
  firstValue: string;
  highMonth: string;
  highValue: string;
  latestMonth: string;
  latestValue: string;
}

export interface PositionTrendLabels {
  /** Texto sobre la cifra del último valor. */
  latest?: string;
  /** Texto cuando no hay saldos mensuales. */
  empty?: string;
  /** Descripción accesible del gráfico. */
  describe?: (description: PositionTrendDescription) => string;
  /** Tipo de elemento que anuncia el lector de pantalla (`aria-roledescription`). */
  role?: string;
  /** Pista accesible de navegación con teclado. */
  keyboardHint?: string;
  /** Anuncio para lectores de pantalla al cambiar de mes con el teclado. */
  monthValue?: (month: string, value: string) => string;
}

export interface PositionTrendProps {
  monthlyBalances: MonthlyBalance[];
  labels?: PositionTrendLabels;
}

interface PositionPoint {
  id: string;
  short: string;
  label: string;
  fullLabel: string;
  /** Mes y año, para el encabezado al pasar el puntero (hay meses repetidos entre años). */
  monthYear: string;
  value: number;
}

const HEIGHT_NARROW = 140;
const HEIGHT_WIDE = 180;
const NARROW_BREAKPOINT = 420;
const TOP = 12;
const RIGHT = 10;

/** Escala lineal (port de `linear` del design system). */
const linear = (d0: number, d1: number, r0: number, r1: number) => (v: number) =>
  d1 === d0 ? (r0 + r1) / 2 : r0 + ((v - d0) / (d1 - d0)) * (r1 - r0);

const f1 = (n: number) => (Math.round(n * 10) / 10).toFixed(1);

/** Curva monótona sin sobrepasos entre datos (port de `monotonePath` del design system). */
function monotonePath(pts: [number, number][]): string {
  const n = pts.length;
  if (n === 0) return "";
  if (n === 1) return `M${f1(pts[0][0])} ${f1(pts[0][1])}`;
  const dx: number[] = [];
  const s: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx.push(pts[i + 1][0] - pts[i][0]);
    s.push((pts[i + 1][1] - pts[i][1]) / (pts[i + 1][0] - pts[i][0] || 1));
  }
  const t: number[] = new Array(n);
  t[0] = s[0];
  t[n - 1] = s[n - 2];
  for (let i = 1; i < n - 1; i++) t[i] = s[i - 1] * s[i] <= 0 ? 0 : (s[i - 1] + s[i]) / 2;
  for (let i = 0; i < n - 1; i++) {
    if (s[i] === 0) {
      t[i] = 0;
      t[i + 1] = 0;
      continue;
    }
    const a = t[i] / s[i];
    const b = t[i + 1] / s[i];
    const h = a * a + b * b;
    if (h > 9) {
      const k = 3 / Math.sqrt(h);
      t[i] = k * a * s[i];
      t[i + 1] = k * b * s[i];
    }
  }
  let d = `M${f1(pts[0][0])} ${f1(pts[0][1])}`;
  for (let i = 0; i < n - 1; i++) {
    const [x0, y0] = pts[i];
    const [x1, y1] = pts[i + 1];
    d += ` C${f1(x0 + dx[i] / 3)} ${f1(y0 + (t[i] * dx[i]) / 3)} ${f1(x1 - dx[i] / 3)} ${f1(y1 - (t[i + 1] * dx[i]) / 3)} ${f1(x1)} ${f1(y1)}`;
  }
  return d;
}

/** Marcas del eje Y: mínimo, mitad, cero, mitad y máximo, con el mismo margen que `BalanceChart`. */
function buildTicks(values: number[]): number[] {
  const minValue = Math.min(0, ...values);
  const maxValue = Math.max(0, ...values);
  const span = Math.max(maxValue - minValue, 1);
  const padding = Math.max(span * 0.18, 1200);
  const domainMin = Math.floor((minValue - padding) / 1000) * 1000;
  const domainMax = Math.ceil((maxValue + padding) / 1000) * 1000;
  return [domainMin, domainMin / 2, 0, domainMax / 2, domainMax]
    .filter((tick, index, ticks) => ticks.indexOf(tick) === index)
    .sort((a, b) => a - b);
}

const formatTick = (value: number) =>
  formatCurrency(value, undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const DEFAULT_DESCRIBE = (d: PositionTrendDescription) =>
  `Monthly position trend: ${d.firstMonth} ${d.firstValue}, high ${d.highMonth} ${d.highValue}, latest ${d.latestMonth} ${d.latestValue}.`;

/**
 * Tendencia de la posición mensual en tarjeta sun: último valor con flecha y curva suave
 * con eje Y desde cero. El SVG usa viewBox del ancho medido, así ocupa 100% del contenedor.
 * Al pasar el puntero (o con flechas, Inicio y Fin) marca el mes con una guía vertical y
 * muestra su monto en el encabezado.
 */
export function PositionTrend({ monthlyBalances, labels }: PositionTrendProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [announcement, setAnnouncement] = useState("");

  const points = useMemo<PositionPoint[]>(
    () =>
      [...monthlyBalances]
        .sort((a, b) => parseDateLocal(a.date).getTime() - parseDateLocal(b.date).getTime())
        .map((item) => {
          const date = parseDateLocal(item.date);
          return {
            id: item.date,
            short: formatDate(date, undefined, { month: "short" }).slice(0, 1),
            label: formatDate(date, undefined, { month: "short" }),
            fullLabel: formatDate(date, undefined, { month: "long" }),
            monthYear: formatDate(date, undefined, { month: "long", year: "numeric" }),
            value: Number(item.balance || 0),
          };
        }),
    [monthlyBalances],
  );

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const update = () => setWidth(Math.round(element.getBoundingClientRect().width));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [points.length]);

  const latestLabel = labels?.latest ?? "Último";
  const emptyLabel = labels?.empty ?? "Sin datos";
  const describe = labels?.describe ?? DEFAULT_DESCRIBE;
  const keyboardHint = labels?.keyboardHint ?? "Use the arrow keys to move between months.";
  const monthValue = labels?.monthValue ?? ((month: string, value: string) => `${month}: ${value}`);

  const shell = "flex w-full flex-col rounded-[var(--ds-radius-lg)] bg-[var(--ds-card-sun)] p-5 text-[var(--ds-on-card)] md:p-6";

  if (points.length === 0) {
    return (
      <div className={`${shell} min-h-[12rem] items-center justify-center`}>
        <p className="text-[var(--ds-on-card-muted)]">{emptyLabel}</p>
      </div>
    );
  }

  const narrow = width < NARROW_BREAKPOINT;
  const chartWidth = Math.max(width, 280);
  const height = narrow ? HEIGHT_NARROW : HEIGHT_WIDE;
  const left = narrow ? 58 : 74;
  const bottom = narrow ? 28 : 30;
  const ticks = buildTicks(points.map((p) => p.value));
  const x = linear(0, points.length - 1, left, chartWidth - RIGHT);
  const y = linear(ticks[0], ticks[ticks.length - 1], height - bottom, TOP);
  const coords = points.map((p, i) => [x(i), y(p.value)] as [number, number]);
  const last = coords[coords.length - 1];
  const latest = points[points.length - 1];
  const first = points[0];
  const active = picked !== null && picked < points.length ? picked : null;
  const shown = active === null ? latest : points[active];
  const columnWidth = points.length > 1 ? x(1) - x(0) : chartWidth - left - RIGHT;

  const onKeyDown = (event: KeyboardEvent<SVGSVGElement>) => {
    const current = active ?? points.length - 1;
    const next =
      event.key === "ArrowRight" ? Math.min(current + 1, points.length - 1)
        : event.key === "ArrowLeft" ? Math.max(current - 1, 0)
          : event.key === "Home" ? 0
            : event.key === "End" ? points.length - 1
              : -1;
    if (next < 0) return;
    event.preventDefault();
    setPicked(next);
    // Solo el teclado anuncia: con el puntero cada columna dispararía un anuncio.
    setAnnouncement(monthValue(points[next].monthYear, formatSignedCurrency(points[next].value)));
  };
  const high = points.reduce((best, p) => (p.value > best.value ? p : best), points[0]);

  const description = describe({
    firstMonth: first.fullLabel,
    firstValue: formatSignedCurrency(first.value),
    highMonth: high.fullLabel,
    highValue: formatSignedCurrency(high.value),
    latestMonth: latest.fullLabel,
    latestValue: formatSignedCurrency(latest.value),
  });

  return (
    <div className={shell}>
      <div className="mb-3 flex items-end justify-between">
        <span className="text-sm capitalize text-[var(--ds-on-card-muted)]">{active === null ? latestLabel : shown.monthYear}</span>
        <b className="font-[var(--font-display)] text-2xl font-medium">
          <span aria-hidden="true">{`${flowArrow(shown.value >= 0 ? "income" : "expense")} `}</span>
          {formatCurrency(shown.value)}
        </b>
      </div>

      <div ref={containerRef} className="w-full" style={{ height }}>
        {width > 0 && (
          <svg
            className="app-focus-ring block h-full w-full overflow-visible"
            width="100%"
            height={height}
            viewBox={`0 0 ${chartWidth} ${height}`}
            preserveAspectRatio="xMidYMid meet"
            role="group"
            aria-roledescription={labels?.role ?? "chart"}
            tabIndex={0}
            aria-label={`${description} ${keyboardHint}`}
            onKeyDown={onKeyDown}
            onPointerLeave={() => setPicked(null)}
            onBlur={() => {
              setPicked(null);
              setAnnouncement("");
            }}
          >
            {ticks.map((tick) => (
              <g key={tick}>
                <line
                  x1={left}
                  x2={chartWidth - RIGHT}
                  y1={y(tick)}
                  y2={y(tick)}
                  stroke="var(--ds-on-card)"
                  strokeOpacity={0.28}
                  strokeDasharray={tick === 0 ? undefined : "3 4"}
                />
                <text x={left - 8} y={y(tick) + 4} textAnchor="end" fontSize={12} fill="var(--ds-on-card-muted)">
                  {formatTick(tick)}
                </text>
              </g>
            ))}
            {points.map((p, i) => (
              <text
                key={p.id}
                x={x(i)}
                y={height - 8}
                fontSize={12}
                fill="var(--ds-on-card-muted)"
                textAnchor={narrow ? "middle" : i === points.length - 1 ? "end" : "middle"}
              >
                {narrow ? p.short : p.label}
              </text>
            ))}
            {active !== null ? (
              <line
                x1={coords[active][0]}
                x2={coords[active][0]}
                y1={TOP}
                y2={height - bottom}
                stroke="currentColor"
                strokeWidth={1.5}
              />
            ) : null}
            <path d={monotonePath(coords)} fill="none" stroke="currentColor" strokeWidth={4} strokeLinejoin="round" />
            {active === null || active === points.length - 1 ? null : (
              <circle cx={coords[active][0]} cy={coords[active][1]} r={6} fill="currentColor" />
            )}
            <circle cx={last[0]} cy={last[1]} r={6} fill="currentColor" />
            {points.map((p, i) => (
              <rect
                key={`hit-${p.id}`}
                x={Math.max(left, x(i) - columnWidth / 2)}
                y={TOP}
                width={points.length === 1 ? columnWidth : i === 0 || i === points.length - 1 ? columnWidth / 2 : columnWidth}
                height={height - bottom - TOP}
                fill="transparent"
                onPointerEnter={() => setPicked(i)}
                onPointerMove={() => setPicked(i)}
              />
            ))}
          </svg>
        )}
      </div>
      <span className="sr-only" aria-live="polite">
        {announcement}
      </span>
    </div>
  );
}
